<template>
    <hox-modal :show-footer="!isLoadingCampaign || !hasErrorLoadingCampaign" @close="close">
        <hox-loading-layer v-if="isLoadingSetCampaignSectionModal" />

        <template #header>Move campaign to section</template>

        <div v-if="isLoadingCampaign" class="set-campaign-section-modal__loading-container">
            <hox-loading-layer :is-full-screen="false" />
        </div>

        <hox-alert v-else-if="hasErrorLoadingCampaign" margin-bottom="none" type="danger">
            <template #title>Uh oh!</template>
            <template #content>
                <p>Something went wrong while getting some data. Try again in a few moments.</p>
            </template>
            <template #actionItems>
                <Button type="primary" @click="refetchCampaign">Retry</Button>
            </template>
        </hox-alert>

        <template v-else>
            <label>Section</label>
            <SectionsSelect v-model="formData.sectionId" :sections="sections" />
            <div
                v-if="hasUnexpectedErrorSettingCampaignSection"
                class="set-campaign-section-modal__unexpected-error-wrapper"
            >
                <Alert banner type="error">
                    <template #desc>
                        There was an unexpected error and the campaign was unable to be moved to the section. Please try
                        again in a few moments.
                    </template>
                </Alert>
            </div>
        </template>

        <template v-if="!isLoadingCampaign && !hasErrorLoadingCampaign" #footer>
            <Button type="primary" :disabled="isLoadingSetCampaignSectionModal" @click="setCampaignSection">
                Save
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import updateCampaignMutation from "@/apollo/mutations/UpdateCampaign.gql";
import campaignQuery from "@/apollo/queries/v2/Campaign.gql";
import SectionsSelect from "@/components/SectionsSelect.vue";
import type { Campaign } from "@/types/types";

export default defineComponent({
    components: {
        SectionsSelect
    },

    props: {
        campaignId: {
            required: true,
            type: String
        },

        sections: {
            required: true,
            type: Array
        }
    },

    emits: ["close", "campaignSectionSet"],

    data() {
        return {
            campaign: undefined as Campaign | undefined,
            formData: {
                sectionId: undefined
            },
            hasErrorLoadingCampaign: false,
            hasUnexpectedErrorSettingCampaignSection: false,
            isLoadingCampaign: 0,
            isLoadingSetCampaignSectionModal: false
        };
    },

    computed: {
        canEditCampaign() {
            return this.$auth.userCan(this.$auth.Actions.CanEditCampaign, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        clientId() {
            return this.$route.params.clientId as string;
        }
    },

    watch: {
        canEditCampaign() {
            if (!this.canEditCampaign) {
                this.$snackbar.warning("You do not have access to edit campaigns");
                this.close();
            }
        }
    },

    methods: {
        close() {
            this.$emit("close");
        },

        refetchCampaign() {
            void this.$apollo.queries.campaign?.refetch();
        },

        async setCampaignSection() {
            if (this.isLoadingSetCampaignSectionModal) {
                return;
            }

            this.isLoadingSetCampaignSectionModal = true;

            try {
                await this.$apollo.mutate({
                    mutation: updateCampaignMutation,
                    variables: {
                        campaignId: this.campaignId,
                        campaign: {
                            sectionId: this.formData.sectionId === "none" ? null : this.formData.sectionId
                        }
                    }
                });
                this.$snackbar.success("Campaign moved to section");

                this.$emit("campaignSectionSet");
                this.close();
            } catch {
                this.hasUnexpectedErrorSettingCampaignSection = true;
            } finally {
                this.isLoadingSetCampaignSectionModal = false;
            }
        }
    },

    apollo: {
        campaign: {
            query: campaignQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingCampaign",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },
            result({ data: { campaign }, error }) {
                if (error) {
                    this.hasErrorLoadingCampaign = true;
                    return;
                }
                this.campaign = campaign;
                this.hasErrorLoadingCampaign = false;
                this.formData = {
                    sectionId: campaign.section ? campaign.section._id : "none"
                };
            }
        }
    }
});
</script>

<style scoped lang="scss">
@use "@style/variables" as *;

.set-campaign-section-modal__unexpected-error-wrapper {
    padding: $spacing 0 0 0;
}

.set-campaign-section-modal__loading-container {
    position: relative;
    height: 300px;
}
</style>
